import { getSessionData } from './cookies';
import { trackEvent, getClientId } from './gtag';

export interface ActivityData {
    sessionId: string;
    action: string;
    page: string;
    data?: Record<string, any>;
    timestamp: Date;
    ipAddress?: string;
    userAgent?: string;
}

class Analytics {
    private activities: ActivityData[] = [];
    private useLocalStorage: boolean;

    constructor() {
        // Use localStorage for analytics in production when backend is not available
        this.useLocalStorage = process.env.REACT_APP_USE_LOCAL_STORAGE === 'true' || !process.env.REACT_APP_API_BASE_URL;
    }

    async track(action: string, page: string, data?: Record<string, any>): Promise<void> {
        const sessionData = getSessionData();

        // Don't track if no session exists yet
        if (!sessionData || !sessionData.sessionId) {
            console.warn('📊 Analytics: Skipping track due to missing session data', { action, page });
            return;
        }

        // Additional validation to prevent undefined values
        if (!action || !page) {
            console.warn('📊 Analytics: Skipping track due to missing action or page', { action, page, sessionId: sessionData.sessionId });
            return;
        }

        const activity: ActivityData = {
            sessionId: sessionData.sessionId,
            action,
            page,
            data,
            timestamp: new Date(),
            // Note: IP address will be captured by the backend from request headers
            // User agent is captured here for completeness
            userAgent: navigator.userAgent
        };

        this.activities.push(activity);

        // Send to backend
        this.sendToBackend(activity);

        // Enhanced GA4 tracking
        await this.sendToGA4(action, page, data);
    }

    private async sendToBackend(activity: ActivityData): Promise<void> {
        // Skip backend requests if using localStorage mode
        if (this.useLocalStorage) {
            console.log('📊 Analytics (localStorage mode):', activity.action, activity.page, activity.data);
            return;
        }

        try {
            await fetch('/api/analytics', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(activity),
            });
        } catch (error) {
            console.error('Failed to send analytics data:', error);
        }
    }

    private async sendToGA4(action: string, page: string, data?: Record<string, any>): Promise<void> {
        try {
            // Get client ID for consistent tracking
            const clientId = await getClientId();

            // Map internal events to GA4 events
            const ga4EventName = this.mapToGA4Event(action);

            if (ga4EventName) {
                trackEvent(ga4EventName, {
                    page_title: page,
                    session_id: getSessionData()?.sessionId,
                    client_id: clientId,
                    ...this.sanitizeDataForGA4(data)
                });
            }
        } catch (error) {
            console.warn('GA4 tracking failed:', error);
        }
    }

    private mapToGA4Event(action: string): string | null {
        const eventMap: Record<string, string> = {
            // Core user journey events
            'page_view': 'page_view',
            'form_start': 'form_start',
            'form_complete': 'form_submit',
            'button_click': 'select_content',

            // Booking and conversion events
            'booking_start': 'begin_checkout',
            'booking_complete': 'purchase',
            'get_quote': 'generate_lead',
            'estimate_requested': 'generate_lead',

            // Product and service selection events
            'product_selection': 'select_item',
            'Product Type Selected': 'select_item',
            'selection_change': 'select_content',
            'dropdown_change': 'select_content',

            // Service type events
            'service_type_selected': 'select_content',

            // Navigation and engagement
            'navigation': 'page_view',
            'modal_open': 'view_item',
            'component_click': 'select_content',

            // Conversion tracking
            'customer_conversion': 'conversion',
            'consultation_booked': 'purchase'
        };

        return eventMap[action] || null;
    }

    private sanitizeDataForGA4(data?: Record<string, any>): Record<string, any> {
        if (!data) return {};

        // Remove PII and sanitize for GA4
        const sanitized: Record<string, any> = {};

        for (const [key, value] of Object.entries(data)) {
            // Skip PII fields
            if (['email', 'phone', 'firstName', 'lastName', 'address'].includes(key)) {
                continue;
            }

            // Include business-relevant non-PII data
            if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
                // Map business-specific fields to GA4 parameters
                if (key === 'productType' || key === 'productName') {
                    sanitized['item_name'] = value;
                } else if (key === 'serviceType') {
                    sanitized['service_type'] = value;
                } else if (key === 'bookingId') {
                    sanitized['transaction_id'] = value;
                } else if (key === 'customerId') {
                    sanitized['user_id'] = `customer_${value}`; // Anonymized user ID
                } else {
                    sanitized[key] = value;
                }
            }
        }

        return sanitized;
    }

    trackPageView(page: string): void {
        this.track('page_view', page);
    }

    // Enhanced GA4-specific lead generation tracking
    trackLeadGeneration(leadType: string, leadValue?: number): void {
        trackEvent('generate_lead', {
            currency: 'AUD',
            value: leadValue || 0,
            lead_type: leadType
        });

        this.track('generate_lead', 'contact_form', { leadType, leadValue });
    }

    trackFormStart(formName: string): void {
        this.track('form_start', formName);
    }

    trackFormComplete(formName: string, data?: Record<string, any>): void {
        this.track('form_complete', formName, data);
    }

    trackFormAbandonment(formName: string, step: string): void {
        this.track('form_abandon', formName, { step });
    }

    trackButtonClick(buttonName: string, page: string): void {
        this.track('button_click', page, { buttonName });
    }

    trackBookingStart(): void {
        this.track('booking_start', 'booking');
    }

    trackBookingComplete(bookingId: string): void {
        this.track('booking_complete', 'booking', { bookingId });
    }

    trackBookingAbandonment(step: string): void {
        this.track('booking_abandon', 'booking', { step });
    }

    trackError(errorType: string, errorMessage: string, data?: Record<string, any>): void {
        this.track('error', errorType, { errorMessage, ...data });
    }

    trackComponentClick(componentId: string, page: string, data?: Record<string, any>): void {
        this.track('component_click', page, { componentId, ...data });
    }

    // Product selection tracking
    trackProductSelection(productType: string, productName: string, page: string = 'details'): void {
        this.track('product_selection', page, { productType, productName });
    }

    // Service type tracking
    trackServiceTypeSelection(serviceType: string, page: string = 'booking'): void {
        this.track('service_type_selected', page, { serviceType });
    }

    // Quote and estimate tracking
    trackQuoteRequest(data?: Record<string, any>): void {
        this.track('get_quote', 'quote', data);
    }

    trackEstimateRequest(data?: Record<string, any>): void {
        this.track('estimate_requested', 'estimate', data);
    }

    // Consultation booking tracking
    trackConsultationBooked(consultationType: string, data?: Record<string, any>): void {
        this.track('consultation_booked', 'booking', { consultationType, ...data });
    }

    trackSelectionChange(componentId: string, page: string, value: string | number | boolean): void {
        this.track('selection_change', page, { componentId, value });
    }

    trackToggle(componentId: string, page: string, isEnabled: boolean): void {
        this.track('toggle', page, { componentId, isEnabled });
    }

    trackDropdownChange(dropdownId: string, page: string, selectedValue: string | number): void {
        this.track('dropdown_change', page, { dropdownId, selectedValue });
    }

    trackInputChange(inputId: string, page: string, value: string): void {
        this.track('input_change', page, { inputId, value: typeof value === 'string' ? value.substring(0, 10) + (value.length > 10 ? '...' : '') : value });
    }

    trackNavigation(from: string, to: string): void {
        this.track('navigation', 'app', { from, to });
    }

    trackModalOpen(modalId: string, page: string): void {
        this.track('modal_open', page, { modalId });
    }

    trackModalClose(modalId: string, page: string): void {
        this.track('modal_close', page, { modalId });
    }

    trackCustomerConversion(conversionType: 'consultation_booked' | 'not_ready' | 'page_abandoned', page: string, data?: Record<string, any>): void {
        this.track('customer_conversion', page, { conversionType, ...data });
    }

    trackPageAbandonment(page: string, data?: Record<string, any>): void {
        const sessionData = getSessionData();

        // Don't send if we don't have a valid session
        if (!sessionData?.sessionId) {
            console.warn('📊 Analytics: Skipping page abandonment due to missing session');
            return;
        }

        // Use sendBeacon for reliable tracking on page unload
        const activity: ActivityData = {
            sessionId: sessionData.sessionId,
            action: 'page_abandonment',
            page,
            data,
            timestamp: new Date(),
            userAgent: navigator.userAgent
        };

        if (navigator.sendBeacon) {
            navigator.sendBeacon('/api/analytics', JSON.stringify(activity));
        } else {
            // Fallback - this might not work reliably on page unload
            this.sendToBackend(activity);
        }
    }

    getActivities(): ActivityData[] {
        return this.activities;
    }
}

export const analytics = new Analytics();
